import { getServerTimeZone } from '../config';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const isoOf = (value) => {
    if (!value) return '';
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString();
    if (typeof value === 'number') {
        const fromEpoch = new Date(value);
        return Number.isNaN(fromEpoch.getTime()) ? '' : fromEpoch.toISOString();
    }
    return String(value);
};

export const formatDate = (value) => {
    const iso = isoOf(value);
    return iso.length >= 10 ? iso.slice(0, 10) : '';
};

const INSTANT_PARTS = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };

let instantFormatter = null;

const getInstantFormatter = () => {
    if (instantFormatter) return instantFormatter;
    try {
        instantFormatter = new Intl.DateTimeFormat('sv-SE', { timeZone: getServerTimeZone(), ...INSTANT_PARTS });
    } catch {
        instantFormatter = new Intl.DateTimeFormat('sv-SE', INSTANT_PARTS);
    }
    return instantFormatter;
};

export const serverDayOf = (value) => {
    const iso = isoOf(value);
    if (!iso) return '';
    if (iso.length < 19) return formatDate(value);
    const instant = new Date(iso);
    return Number.isNaN(instant.getTime()) ? '' : getInstantFormatter().format(instant).slice(0, 10);
};

export const formatDateTime = (value) => {
    const iso = isoOf(value);
    if (iso.length < 19) return formatDate(value);
    const instant = new Date(iso);
    return Number.isNaN(instant.getTime()) ? '' : getInstantFormatter().format(instant);
};

export const formatDayLong = (value) => {
    const day = formatDate(value);
    if (!day) return '';
    const month = MONTHS[Number(day.slice(5, 7)) - 1];
    return month ? `${month} ${Number(day.slice(8, 10))}, ${day.slice(0, 4)}` : day;
};

export const formatDayShort = (value) => {
    const long = formatDayLong(value);
    return long ? `${long.slice(0, 3)}${long.slice(long.indexOf(' '))}` : '';
};

export const formatMonthDay = (value) => {
    const short = formatDayShort(value);
    return short ? short.slice(0, short.indexOf(',')) : '';
};

export const toLocalDay = (date = new Date()) => {
    if (typeof date === 'string') return formatDate(date);
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const activityDate = new Date(date);
    if (Number.isNaN(activityDate.getTime())) return '';
    const diffMs = now - activityDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return serverDayOf(activityDate);
};

export const formatCommentTime = (value) => {
    const iso = isoOf(value);
    if (!iso) return '';
    const instant = new Date(iso);
    if (Number.isNaN(instant.getTime())) return '';
    const diffMs = Date.now() - instant.getTime();
    if (diffMs < 0 || diffMs >= 48 * 3600000) return getInstantFormatter().format(instant);
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMs / 3600000);
    return `${diffHours}h ago`;
};

export const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}${month}`;
};

export const getLastMonth = () => {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth();

    if (month === 0) {
        month = 12;
        year -= 1;
    }

    month = String(month).padStart(2, '0');
    return `${year}${month}`;
};

export const addDays = (date, days) => {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
        const anchored = new Date(`${date.slice(0, 10)}T12:00:00.000Z`);
        anchored.setUTCDate(anchored.getUTCDate() + days);
        return anchored.toISOString().slice(0, 10);
    }
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const addMonthsToDay = (value, months) => {
    const day = formatDate(value);
    if (!day) return '';
    const total = Number(day.slice(0, 4)) * 12 + Number(day.slice(5, 7)) - 1 + months;
    const year = Math.floor(total / 12);
    const month = total - year * 12;
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const dayOfMonth = Math.min(Number(day.slice(8, 10)), lastDay);
    return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(dayOfMonth).padStart(2, '0')}`;
};

export const differenceInDays = (date1, date2) => {
    const oneDay = 1000 * 60 * 60 * 24; // milliseconds in one day
    const diffInTime = new Date(date1).getTime() - new Date(date2).getTime();
    return Math.round(diffInTime / oneDay);
};

export const formatDueLabel = (value) => {
    const due = formatDate(value);
    if (!due) return 'No due date';
    const diffDays = differenceInDays(due, toLocalDay());
    if (diffDays < 0) return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} late`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
};

const monthKeyFormatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

export const formatMonthKey = (monthKey) => {
    if (typeof monthKey !== 'string' || !/^\d{6}$/.test(monthKey)) return '';
    const month = Number(monthKey.slice(4));
    if (month < 1 || month > 12) return '';
    return monthKeyFormatter.format(new Date(Number(monthKey.slice(0, 4)), month - 1, 1));
};
